import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DwConfigService, DwConfigServiceToken, DwEventService, DwOrmDataService, DwSecurityUserService, SuppressErrorMessagesHeader } from '@devwareapps/devware-cap';
import { Subscription, timer } from 'rxjs';
import { distinctUntilChanged, map, startWith, take } from 'rxjs/operators';
import { AppMetaData } from '../../../meta-data/app-meta-data.service';

interface DonationPreferences { donationsAnonymous: boolean; includeDonationsInTeamTotals: boolean; }
interface DonationPreferencesWire extends Partial<DonationPreferences> {
    DonationsAnonymous?: boolean;
    IncludeDonationsInTeamTotals?: boolean;
}

/** Older CAP hosts use PascalCase. Never treat a missing response field as false. */
export function readDonationPreferences(value: DonationPreferencesWire): DonationPreferences {
    const anonymous = value?.donationsAnonymous ?? value?.DonationsAnonymous;
    const teams = value?.includeDonationsInTeamTotals ?? value?.IncludeDonationsInTeamTotals;
    if (typeof anonymous !== 'boolean' || typeof teams !== 'boolean') { throw new Error('Invalid donation preferences response.'); }
    return {donationsAnonymous: anonymous, includeDonationsInTeamTotals: !anonymous && teams};
}

@Component({ selector: 'app-donation-preferences', template: `
  <section class="donation-preferences" aria-label="Donation preferences" [attr.aria-busy]="loading || saving">
    <p *ngIf="loading || saving" role="status"><span class="preference-spinner" aria-hidden="true"></span>{{ loading ? 'Loading settings…' : 'Saving settings…' }}</p>
    <p *ngIf="!loading && !error" class="preference-summary">{{ summary }}
      <button type="button" class="preference-change" [disabled]="disabled || saving" (click)="expanded = !expanded" [attr.aria-expanded]="expanded">{{ expanded ? 'Done' : 'Change' }}</button>
    </p>
    <ng-container *ngIf="expanded && !loading">
      <fieldset [disabled]="disabled || saving || !!error">
        <legend>Donation preferences</legend>
        <p class="preference-help">These settings apply to your past and future donations from all streaks.</p>
        <label><input type="checkbox" [checked]="settings.donationsAnonymous" (change)="change('donationsAnonymous', $event.target.checked)"> Show my donations anonymously</label>
        <label *ngIf="!settings.donationsAnonymous"><input type="checkbox" [checked]="settings.includeDonationsInTeamTotals" (change)="change('includeDonationsInTeamTotals', $event.target.checked)"> Include my donations in team totals</label>
      </fieldset>
      <p *ngIf="settings.donationsAnonymous" class="preference-help">Anonymous donations do not count toward team totals.</p>
    </ng-container>
    <p *ngIf="error" role="alert">{{ error }} <button type="button" (click)="load()">Check settings</button></p>
    <span *ngIf="saved && !saving && !error" class="preference-saved" role="status"><span aria-hidden="true">✓</span> Saved</span>
  </section>`, styles: [`
    .donation-preferences{margin-top:1rem;padding-top:1rem;border-top:1px solid var(--dw-fitness-border,#ddd)}
    p{font-size:.9rem;margin:.5rem 0}label{display:flex;align-items:center;gap:.5rem;margin:.75rem 0}
    fieldset{border:0;padding:0;margin:0}legend{font-size:1rem;font-weight:600}
    input{width:auto;flex:none}.preference-help{color:var(--text-secondary,#555)}
    .preference-change{background:none;border:0;padding:0 .25rem;color:var(--primary,#235f43);text-decoration:underline;cursor:pointer;font:inherit}
    .preference-saved{display:inline-flex;align-items:center;gap:.35rem;margin-top:.5rem;padding:.2rem .65rem;border-radius:999px;background:#e8f3ec;color:#245a37;font-size:.8rem;font-weight:600}
    .preference-spinner{display:inline-block;width:.9rem;height:.9rem;border:2px solid #ddd;border-top-color:currentColor;border-radius:50%;margin-right:.5rem;animation:preference-spin .8s linear infinite}
    @keyframes preference-spin{to{transform:rotate(360deg)}}
    @media(prefers-reduced-motion:reduce){.preference-spinner{animation:none}}
  `] })
export class DonationPreferencesComponent implements OnInit, OnDestroy {
    @Input() disabled = false;
    @Output() busyChange = new EventEmitter<boolean>(true);
    settings: DonationPreferences = {donationsAnonymous: false, includeDonationsInTeamTotals: true};
    loading = true; saving = false; saved = false; expanded = false; error = '';
    private _subscriptions = new Subscription();
    private _request = new Subscription();
    private _savedNotice = new Subscription();
    private _scope = 0;
    private readonly _headers = {[SuppressErrorMessagesHeader]: 'true'};
    constructor(private _http: HttpClient, @Inject(DwConfigServiceToken) private _config: DwConfigService,
        private _security: DwSecurityUserService, private _events: DwEventService) {}
    private get url(): string { return this._config.coreConfig.apiRoot.replace(/\/$/, '') + '/fitness/profile/donation-preferences'; }
    get summary(): string {
        if (this.settings.donationsAnonymous) { return 'Your donations will show as Anonymous and will not count toward team totals.'; }
        if (!this.settings.includeDonationsInTeamTotals) { return 'Your donations will show your name on the leaderboard and will not count toward team totals.'; }
        return 'Your donations will show your name on the leaderboard and apply to any teams you join.';
    }
    ngOnInit(): void {
        this._subscriptions.add(this._security.securityContextChanged().pipe(startWith(null), map(() => {
            const context = this._security.securityContext;
            return JSON.stringify([context?.ApiRoot, context?.AuthenticatedUser?.UserId, context?.ApplicationUser?.UserId]);
        }), distinctUntilChanged()).subscribe(() => { this.expanded = false; this.load(); }));
    }
    load(): void {
        this.clearSavedNotice();
        this._request.unsubscribe(); this._request = new Subscription();
        const scope = ++this._scope;
        this.loading = true; this.saving = false; this.saved = false; this.error = ''; this.busyChange.emit(true);
        this._request.add(this._http.get<DonationPreferencesWire>(this.url, {headers: this._headers}).pipe(take(1), map(readDonationPreferences)).subscribe(settings => {
            if (scope !== this._scope) { return; }
            this.settings = settings; this.loading = false; this.busyChange.emit(false);
        }, () => {
            if (scope !== this._scope) { return; }
            this.loading = false; this.error = 'We couldn’t load your donation settings. Please try again.';
        }));
    }
    change(field: keyof DonationPreferences, value: boolean): void {
        if (this.disabled || this.loading || this.saving || this.error) { return; }
        if (field === 'includeDonationsInTeamTotals' && this.settings.donationsAnonymous) { return; }
        if (this.settings[field] === value) { return; }
        this.clearSavedNotice();
        const scope = this._scope;
        this.settings = readDonationPreferences({...this.settings, [field]: value});
        this.saving = true; this.saved = false; this.busyChange.emit(true);
        this._request.add(this._http.put<DonationPreferencesWire>(this.url, this.settings, {headers: this._headers}).pipe(take(1), map(readDonationPreferences)).subscribe(settings => {
            if (scope !== this._scope) { return; }
            this.settings = settings; this.saving = false; this.saved = true;
            this._savedNotice = timer(3000).subscribe(() => this.saved = false);
            // Refresh cached profile and donation boards after this custom endpoint's save.
            this._events.publishEvent(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.UserProfile.ItemDetail.itemName), {});
            this.busyChange.emit(false);
        }, () => {
            if (scope !== this._scope) { return; }
            this.saving = false; this.error = 'We couldn’t confirm your donation settings. Check them before continuing.';
        }));
    }
    private clearSavedNotice(): void { this._savedNotice.unsubscribe(); this.saved = false; }
    ngOnDestroy(): void { this._scope++; this.clearSavedNotice(); this._request.unsubscribe(); this._subscriptions.unsubscribe(); this.busyChange.emit(false); }
}
